/**
 * A block definition as the editor edits it (D20, requirement 8.7).
 *
 * The editor edits a design document, and a definition is one in all but
 * name: nodes and edges from the same schema (M1 design §5.3). What a
 * definition adds is its boundary, the exposed ports, which is held beside the
 * editor rather than inside it and folded back in when the draft is written.
 */
import type { Anchor, DesignDocument, ExposedPort, GroupDefinitionDocument } from "../design/document";

// Mirrors core/blocks/extract.py: the collapsed symbol grows with its ports.
const BLOCK_WIDTH = 96;
const PORT_PITCH = 16;
const MIN_BLOCK_HEIGHT = 48;

/** The editor's view of a definition: its graph, and no blocks within it (D16). */
export function asEditable(definition: GroupDefinitionDocument): DesignDocument {
  return { nodes: definition.nodes ?? [], edges: definition.edges ?? [] };
}

function portRefs(document: DesignDocument): Set<string> {
  return new Set(
    (document.nodes ?? []).flatMap((node) => (node.ports ?? []).map((port) => `${node.local_id}.${port.local_id}`)),
  );
}

function connectedRefs(document: DesignDocument): Set<string> {
  return new Set((document.edges ?? []).flatMap((edge) => [edge.from_port, edge.to_port]));
}

/**
 * Ports no channel inside the block uses and that are not dead-ended: the
 * ones the outside could connect to.
 */
export function freePorts(document: DesignDocument): string[] {
  const connected = connectedRefs(document);
  const closed = new Set(
    (document.nodes ?? []).flatMap((node) =>
      (node.ports ?? []).filter((port) => port.dead_end).map((port) => `${node.local_id}.${port.local_id}`),
    ),
  );
  return [...portRefs(document)].filter((ref) => !connected.has(ref) && !closed.has(ref));
}

/** Exposed ports a channel inside the block already occupies, so nothing outside can use. */
export function occupiedExposed(document: DesignDocument, exposed: readonly ExposedPort[]): string[] {
  const connected = connectedRefs(document);
  return exposed.filter((port) => connected.has(port.internal_ref)).map((port) => port.name);
}

/** `valve_1.port_a` -> `valve_1_port_a`, made unique among `taken`. */
export function exposedName(ref: string, taken: Iterable<string>): string {
  const used = new Set(taken);
  const stem = ref.replace(/\./g, "_");
  if (!used.has(stem)) return stem;
  let ordinal = 2;
  while (used.has(`${stem}_${ordinal}`)) ordinal += 1;
  return `${stem}_${ordinal}`;
}

/**
 * Where each exposed port sits on the collapsed symbol: west for a port on a
 * component left of the ports' middle, east otherwise, top to bottom by the
 * component's position. The server's rule for a block saved from a selection,
 * applied again when the boundary changes. Presentation only.
 */
export function layoutExposed(document: DesignDocument, exposed: readonly ExposedPort[]): ExposedPort[] {
  const position = (ref: string) => {
    const node = (document.nodes ?? []).find((candidate) => candidate.local_id === ref.split(".")[0]);
    return node?.schematic_position ?? { x: 0, y: 0 };
  };
  if (exposed.length === 0) return [];
  const placed = exposed.map((port, index) => ({ port, index, ...position(port.internal_ref) }));
  const middle = placed.reduce((sum, entry) => sum + entry.x, 0) / placed.length;
  const byHeight = (a: (typeof placed)[number], b: (typeof placed)[number]) => a.y - b.y || a.index - b.index;
  const west = placed.filter((entry) => entry.x < middle).sort(byHeight);
  const east = placed.filter((entry) => entry.x >= middle).sort(byHeight);
  const anchored = new Map<ExposedPort, Anchor>();
  for (const [side, entries, x] of [
    ["west", west, 0],
    ["east", east, 1],
  ] as const) {
    entries.forEach((entry, index) =>
      anchored.set(entry.port, { x, y: (index + 1) / (entries.length + 1), direction: side }),
    );
  }
  return exposed.map((port) => ({ ...port, anchor: anchored.get(port) }));
}

/**
 * The definition content to write: the editor's graph with the boundary
 * folded back in. Exposed ports whose component has been deleted are left
 * out of what is written, but kept by the caller, so an undo brings them back.
 */
export function definitionContent(
  base: GroupDefinitionDocument,
  document: DesignDocument,
  exposed: readonly ExposedPort[],
  summary: string,
): GroupDefinitionDocument {
  const ports = portRefs(document);
  const kept = exposed.filter((port) => ports.has(port.internal_ref));
  const west = kept.filter((port) => (port.anchor?.x ?? 1) < 0.5).length;
  const height = Math.max(MIN_BLOCK_HEIGHT, PORT_PITCH * (Math.max(west, kept.length - west) + 1));
  return {
    ...base,
    summary,
    // A pasted block would be a block within a block, which definitions cannot hold (D16).
    nodes: (document.nodes ?? []).map((node) => (node.group_instance ? { ...node, group_instance: null } : node)),
    edges: document.edges ?? [],
    exposed_ports: kept,
    symbol: { glyph: "block", ...base.symbol, width: base.symbol?.width ?? BLOCK_WIDTH, height },
  };
}
